import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Stack, Typography } from '@mui/material';
import { outcomeColor, payoutHint } from '../lib/outcomeColors';
import type { Market } from '../schemas/market.schema';
import { colors, mono } from '../themes/theme';

interface OutcomeChoiceProps {
    market: Market;
    selectedId: number | null;
    onSelect: (outcomeId: number) => void;
}

/**
 * The actual choice, as buttons that look like buttons.
 *
 * Selection used to happen by tapping a progress bar, which nobody reads as
 * something to press. These are full-width rows with a radio mark, the label,
 * and what a win pays -- so what to do is obvious before what the odds are.
 */
export function OutcomeChoice({ market, selectedId, onSelect }: OutcomeChoiceProps) {
    const total = market.outcomes.length;

    return (
        <Stack spacing={1}>
            <Typography variant="overline" sx={{ color: colors.muted }}>
                {total === 2 ? 'Which way?' : 'Pick one'}
            </Typography>

            {market.outcomes.map((outcome, index) => {
                const selected = selectedId === outcome.id;
                const tint = outcomeColor(index, total);
                const pays = payoutHint(outcome, market);

                return (
                    <Box
                        key={outcome.id}
                        component="button"
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onSelect(outcome.id)}
                        sx={{
                            width: '100%',
                            minHeight: 60,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.75,
                            py: 1.25,
                            font: 'inherit',
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderRadius: '3px',
                            border: `2px solid ${selected ? tint : colors.line}`,
                            bgcolor: selected ? `${tint}22` : colors.surface,
                            color: colors.ink,
                            transition: 'border-color 120ms ease, background-color 120ms ease',
                        }}
                    >
                        {selected ? (
                            <CheckCircleIcon sx={{ color: tint }} />
                        ) : (
                            <RadioButtonUncheckedIcon sx={{ color: colors.muted }} />
                        )}

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '1.05rem',
                                    color: selected ? tint : colors.ink,
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {outcome.label}
                            </Typography>
                            <Typography
                                sx={{ fontFamily: mono, fontSize: '0.7rem', color: colors.muted }}
                            >
                                {pays === null
                                    ? 'no bets on this yet'
                                    : `pays ${pays.toFixed(1)}× if it wins`}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
}
